import {DeleteFilled} from "@ant-design/icons";
import {Button, Modal, Tooltip, message} from "antd";
import React, {FC, memo, useState} from "react";
import {red} from "@ant-design/colors";
import {
    GetBookedAttendeesByCourseIdDocument,
    GetBookingsByCourseDocument,
    GetBookingsByCourseQuery,
    GetBookingsOverviewOptimizedDocument,
    GetBookinsDocument,
    GetCourseByIdDocument,
    GetCourseDetailsByIdDocument,
    GetUnsettledBookingsDocument,
    GetWaitlistByCourseDocument,
    useDeleteSelectionOfWaitlistMutation,
} from "../../../generated/graphql";
import {resolveServerErrorMessage} from "../../../helpers/resolveServerErrorMessage";
import {useStaffVisibility} from "../../../hooks/useStaffVisibility";
import {StaffVisibilityLevel} from "../../../../shared/models/staffVisibility";

type BulkDeleteWaitlistProps = {
    currentCourseId: string;
    key: number;
    selectedBookings: GetBookingsByCourseQuery["getBookingsByCourse"]["items"];
    btnClassName?: string;
    onFinished: () => void;
};

const BulkDeleteWaitlist: FC<BulkDeleteWaitlistProps> = ({currentCourseId, key, selectedBookings, btnClassName, onFinished}) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    // Sichtbarkeitsregel „Buchungsaktionen: Nein“ (#1188): ausgewählte Wartelistenplätze nicht löschen.
    const bookingActionsDisabled = useStaffVisibility("bookingActions") === StaffVisibilityLevel.NoAccess;
    const [deleteSelectionOfWaitlist] = useDeleteSelectionOfWaitlistMutation({
        refetchQueries: [
            "GetBookingsByCourse",
            {query: GetBookingsByCourseDocument, variables: {id: currentCourseId}},
            "GetCourseById",
            {query: GetCourseByIdDocument, variables: {id: currentCourseId}},
            "GetBookins",
            {query: GetBookinsDocument},
            "GetBookingsOverviewOptimized",
            {query: GetBookingsOverviewOptimizedDocument},
            "GetUnsettledBookings",
            {query: GetUnsettledBookingsDocument},
            "GetSettledBookings",
            "GetBookedAttendeesByCourseId",
            {query: GetBookedAttendeesByCourseIdDocument, variables: {courseId: currentCourseId}},
            "GetWaitlistByCourse",
            {query: GetWaitlistByCourseDocument, variables: {id: currentCourseId}},
            "GetCourseDetailsById",
            {query: GetCourseDetailsByIdDocument, variables: {id: currentCourseId}},
        ],
    });

    const handleDelete = async () => {
        try {
            setLoading(true);
            message.loading({
                key: "deleteWaitlistMsg",
                content: "Lösche Wartelistenplätze...",
            });
            await deleteSelectionOfWaitlist({
                variables: {
                    bookingIds: selectedBookings.map((booking) => booking.id),
                },
            }).then((res) => {
                if (res.data?.deleteSelectionOfWaitlist) {
                    const count = res.data.deleteSelectionOfWaitlist;

                    message.success({
                        key: "deleteWaitlistMsg",
                        content: count > 1 ? `${count} Wartelistenplätze erfolgreich gelöscht` : `Wartelistenplatz erfolgreich gelöscht`,
                    });

                    setLoading(false);
                    setOpen(false);
                }
            });
        } catch (error) {
            // Server-Guards (z.B. MOLLIE_WAITLIST_PAID_CANNOT_DELETE) erklären in
            // der GraphQL-Message, warum nicht gelöscht werden kann und was zu tun
            // ist. Die generische Meldung bleibt Fallback, wenn keine Server-Message
            // vorliegt (Netzwerkfehler o.ä.).
            await message.error({
                key: "deleteWaitlistMsg",
                content: resolveServerErrorMessage(error, "Fehler beim Löschen der Wartelistenplätze"),
            });
            console.error(error);
        } finally {
            setTimeout(() => {
                setLoading(false);
                message.destroy("deleteWaitlistMsg");
                onFinished();
                setOpen(false);
            }, 2000);
        }
    };

    if (bookingActionsDisabled) {
        return null;
    }

    const count = selectedBookings.length > 1 ? selectedBookings.length : "";
    const articlePlural = selectedBookings.length > 1 ? "die" : "der";
    const waitlistPlural = selectedBookings.length > 1 ? "Wartelistenplätze" : "Wartelistenplatz";
    const modalText = `Sollen ${articlePlural} ausgewählten ${count} ${waitlistPlural} dauerhaft gelöscht werden?`;

    if (open) {
        return (
            <Modal
                title="Wartelistenplätze löschen"
                open={open}
                onCancel={() => setOpen(false)}
                okText={loading ? "löschen..." : "Jetzt löschen"}
                okType="danger"
                okButtonProps={{
                    loading,
                    block: loading,
                    style: {margin: "auto"},
                }}
                cancelButtonProps={{
                    disabled: loading,
                    hidden: loading,
                    style: {marginRight: 7},
                }}
                bodyStyle={{textAlign: "center"}}
                onOk={handleDelete}
                destroyOnClose
            >
                {modalText}
            </Modal>
        );
    }

    return (
        <Tooltip title="Ausgewählte Wartelistenplätze löschen">
            <Button key={key} onClick={() => setOpen(true)} size="small" type="link" style={{color: red[3]}} shape="circle" className={btnClassName}>
                <DeleteFilled />
            </Button>
        </Tooltip>
    );
};

export default memo(BulkDeleteWaitlist);
